import { BillingInterval, BillingPlanKey, Program, TrialTier } from '../models/billing.models';
import { User } from '../models/user.models';

/** Julien's app user id on staging and production. The API enforces the same id. */
export const UNLIMITED_TEAM_CREATOR_USER_ID = 22;

/** True only for the founding-Unlimited owner who may add squads. */
export const isUnlimitedTeamCreator = (): boolean => {
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return false;
    return (JSON.parse(raw) as User).id === UNLIMITED_TEAM_CREATOR_USER_ID;
  } catch {
    return false;
  }
};

const PENDING_PAID_TEAM_KEY = 'coelab_pending_paid_team';

/** Team details held until the card payment for a returning coach succeeds. */
export interface PendingPaidTeam {
  programId?: number;
  subscriptionId?: string;
  planKey?: BillingPlanKey;
  billingInterval?: BillingInterval;
  teamName: string;
  teamColor: string;
  teamDescription: string;
}

export const savePendingPaidTeam = (draft: PendingPaidTeam): void => {
  window.sessionStorage.setItem(PENDING_PAID_TEAM_KEY, JSON.stringify(draft));
};

export const readPendingPaidTeam = (): PendingPaidTeam | null => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_PAID_TEAM_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as PendingPaidTeam;
    if (!draft?.teamName || (!draft.programId && !draft.planKey)) return null;
    return draft;
  } catch {
    return null;
  }
};

export const clearPendingPaidTeam = (): void => {
  window.sessionStorage.removeItem(PENDING_PAID_TEAM_KEY);
};

/** True when the signed-in account, or the given address, ends with .edu. */
export const isAcademicEmail = (email?: string | null): boolean => {
  let value = email;
  if (value == null) {
    try {
      const raw = window.localStorage.getItem('user');
      value = raw ? (JSON.parse(raw) as User).email : '';
    } catch {
      value = '';
    }
  }
  return !!value && value.trim().toLowerCase().endsWith('.edu');
};

export const formatMoney = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return `$${amount.toLocaleString('en-US')}`;
};

export const formatCents = (cents: number | null | undefined): string => {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const intervalLabel = (interval: BillingInterval | string): string => (interval === 'year' ? 'year' : 'month');

export const intervalShort = (interval: BillingInterval | string): string => (interval === 'year' ? '/yr' : '/mo');

export interface IntervalTotals {
  month: number;
  year: number;
}

/** What this subscription is charging now. A free trial is not included until it converts. */
export const programCharge = (program: Program): number => {
  if (program.status !== 'active' && program.status !== 'past_due' && program.status !== 'unpaid') return 0;
  const extra = (program.extraTeamPrice ?? 0) * (program.extraTeams || 0);
  return (program.listPrice ?? 0) + extra;
};

/** Sum of open subscriptions, split by interval so monthly and yearly stay separate. */
export const subscriptionTotals = (programs: Program[]): IntervalTotals =>
  programs.reduce(
    (totals, program) => {
      const amount = programCharge(program);
      if (program.billingInterval === 'year') totals.year += amount;
      else totals.month += amount;
      return totals;
    },
    { month: 0, year: 0 }
  );

export const formatIntervalTotals = (totals: IntervalTotals): string => {
  const parts: string[] = [];
  if (totals.month > 0) parts.push(`${formatMoney(totals.month)}/month`);
  if (totals.year > 0) parts.push(`${formatMoney(totals.year)}/year`);
  return parts.join(' and ') || '$0';
};

export const isAcademicPlan = (planKey: string | null | undefined): boolean =>
  planKey === 'academic' || planKey === 'academic_school';

const isCatalogIntervalPlan = (planKey: string | null | undefined): boolean =>
  planKey === 'small_team' || planKey === 'program';

/**
 * The cycle this account bills on.
 * Academic locks it to yearly. Otherwise it is the interval of the first
 * Small or Large subscription. GET /programs is newest first, so that row is last.
 */
export const accountBillingInterval = (programs: Program[]): BillingInterval | null => {
  if (programs.some((program) => isAcademicPlan(program.planKey))) return 'year';
  const catalog = programs.filter((program) => isCatalogIntervalPlan(program.planKey));
  if (catalog.length === 0) return null;
  return catalog[catalog.length - 1].billingInterval;
};

export const CATALOG_PLAN_LABELS: Record<string, string> = {
  small_team: 'Small Team',
  program: 'Large Team',
  academic: 'Academic',
  academic_school: 'Academic',
};

/** Free trial past its end date but not yet converted (webhooks/cron haven't flipped it). */
export const isTrialExpired = (program: Program | null | undefined): boolean =>
  !!program &&
  program.status === 'trialing' &&
  !!program.trialEndsAt &&
  new Date(program.trialEndsAt).getTime() < Date.now();

/** Teams are locked until the coach pays: unpaid program, exhausted retries, or ended trial. */
export const isProgramLocked = (program: Program | null | undefined): boolean =>
  !!program && (program.status === 'incomplete' || program.status === 'unpaid' || isTrialExpired(program));

export const programStatusChip = (
  program: Program
): { label: string; tone: 'success' | 'warning' | 'danger' | 'muted' | 'info' } => {
  if (isTrialExpired(program)) return { label: 'Trial ended', tone: 'warning' };
  if (program.cancelAtPeriodEnd) return { label: 'Cancels at period end', tone: 'warning' };
  if (program.status === 'active') return { label: 'Active', tone: 'success' };
  if (program.status === 'trialing') return { label: 'Free trial', tone: 'info' };
  if (program.status === 'incomplete') return { label: 'Payment needed', tone: 'warning' };
  if (program.status === 'past_due') return { label: 'Past due', tone: 'danger' };
  if (program.status === 'unpaid') return { label: 'Payment failed', tone: 'danger' };
  if (program.status === 'canceled') return { label: 'Canceled', tone: 'muted' };
  return { label: program.status, tone: 'muted' };
};

/** Human length of a free trial, e.g. "six months" or "one month". */
export const trialLengthPhrase = (trialDays: number | null | undefined): string => {
  if (!trialDays) return '';
  if (trialDays >= 180) return 'six months';
  if (trialDays >= 28 && trialDays <= 31) return 'one month';
  return `${trialDays} days`;
};

export const isEarlyAccessTrial = (trialTier: TrialTier | null | undefined): boolean => trialTier === 'early_access';

/** "up to 50 members (coaches and athletes)" */
export const memberCapPhrase = (membersPerTeam: number | null | undefined): string =>
  `up to ${membersPerTeam || 0} members (coaches and athletes)`;
